import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import MenuPermissionsEditor from '../components/MenuPermissionsEditor';
import { useLanguage } from '../context/LanguageContext';

const Agents = () => {
  const { t, lang } = useLanguage();
  const f = t('agent_form');
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  
  // 🔐 Permissions Modal State - NEW
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedAgentForPermissions, setSelectedAgentForPermissions] = useState(null);
  
  // ✅ Updated formData with ALL new fields + country as array
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    phone: '',
    phone_whatsapp: '',
    role: 'agent',
    language_skills: [],
    departments: [],
    modules: [],
    service_type: 'all',
    country: [],  // ✅ Array for multi-select
    availability_time_from: '09:00',
    availability_time_to: '17:00',
    availability_days: [],
    max_concurrent_conversations: 5,
    auto_assign: true,
    assign_technical: false,
    assign_sales: false,
    manage_others_bookings: false,
    booking_enabled: false,
    can_view_all_conversations: false,
    can_view_leads: false,
    allow_marketing: false,
    allow_vsm: false,
    default_module: 'comms',
    priority_score: 50,
    timezone: 'Africa/Cairo',
    is_active: true
  });

  // 🔔 Helpdesk Notification Settings State
  const [notificationSettings, setNotificationSettings] = useState({
    is_active: false,
    notify_by_email: false,
    notify_by_whatsapp: false,
    receive_new_ticket: false,
    receive_ticket_assigned: false,
    receive_client_reply: false,
    receive_agent_reply: false,
    receive_status_change: false,
    receive_ticket_unassigned: false,
    receive_sla_escalation: false,
    receive_priority_change: false,
    receive_dept_transfer: false,
    receive_ticket_reopened: false,
    receive_dept_delayed: false,
    receive_dept_overdue: false,
    receive_dept_critical: false,
    department_ids: []
  });
  const [helpdeskDepartments, setHelpdeskDepartments] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  
  const [attachments, setAttachments] = useState([]);
  const [isNewClient, setIsNewClient] = useState(false);
  
  const navigate = useNavigate();
  
  // Get current user to check if admin
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchAgents();
    fetchHelpdeskDepartments();
  }, []);

  // Fetch helpdesk departments for notification settings
  const fetchHelpdeskDepartments = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch('/api/v1/helpdesk-agent/departments/active', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setHelpdeskDepartments(data.departments || []);
        }
      }
    } catch (err) {
      console.error('Error fetching helpdesk departments:', err);
    }
  };

  // Fetch notification settings for an agent
  const fetchNotificationSettings = async (agentId) => {
    const token = localStorage.getItem('token');
    setLoadingNotifications(true);
    try {
      const response = await fetch(`/api/v1/helpdesk-agent/notifications/agent/${agentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.settings) {
          setNotificationSettings({
            is_active: data.settings.is_active === 1,
            notify_by_email: data.settings.notify_by_email === 1,
            notify_by_whatsapp: data.settings.notify_by_whatsapp === 1,
            receive_new_ticket: data.settings.receive_new_ticket === 1,
            receive_ticket_assigned: data.settings.receive_ticket_assigned === 1,
            receive_client_reply: data.settings.receive_client_reply === 1,
            receive_agent_reply: data.settings.receive_agent_reply === 1,
            receive_status_change: data.settings.receive_status_change === 1,
            receive_ticket_unassigned: data.settings.receive_ticket_unassigned === 1,
            receive_sla_escalation: data.settings.receive_sla_escalation === 1,
            receive_priority_change: data.settings.receive_priority_change === 1,
            receive_dept_transfer: data.settings.receive_dept_transfer === 1,
            receive_ticket_reopened: data.settings.receive_ticket_reopened === 1,
            receive_dept_delayed: data.settings.receive_dept_delayed === 1,
            receive_dept_overdue: data.settings.receive_dept_overdue === 1,
            receive_dept_critical: data.settings.receive_dept_critical === 1,
            department_ids: data.departments || []
          });
        } else {
          // No settings found, reset to defaults
          resetNotificationSettings();
        }
      }
    } catch (err) {
      console.error('Error fetching notification settings:', err);
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Save notification settings
  const saveNotificationSettings = async (agentId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/api/v1/helpdesk-agent/notifications/agent/${agentId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(notificationSettings)
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        console.error('Failed to save notification settings:', data.message);
      }
    } catch (err) {
      console.error('Error saving notification settings:', err);
    }
  };

  const resetNotificationSettings = () => {
    setNotificationSettings({
      is_active: false,
      notify_by_email: false,
      notify_by_whatsapp: false,
      receive_new_ticket: false,
      receive_ticket_assigned: false,
      receive_client_reply: false,
      receive_agent_reply: false,
      receive_status_change: false,
      receive_ticket_unassigned: false,
      receive_sla_escalation: false,
      receive_priority_change: false,
      receive_dept_transfer: false,
      receive_ticket_reopened: false,
      receive_dept_delayed: false,
      receive_dept_overdue: false,
      receive_dept_critical: false,
      department_ids: []
    });
  };

  const toggleNotificationDepartment = (deptId) => {
    setNotificationSettings(prev => ({
      ...prev,
      department_ids: prev.department_ids.includes(deptId)
        ? prev.department_ids.filter(id => id !== deptId)
        : [...prev.department_ids, deptId]
    }));
  };

const fetchAgents = async () => {
  const token = localStorage.getItem('token');
  if (!token) {
    navigate('/login');
    return;
  }

  try {
    const response = await fetch('/api/v1/agents', {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        // ✅ Parse JSON fields for each agent
        const parsedAgents = data.data.map(agent => ({
          ...agent,
          language_skills: typeof agent.language_skills === 'string' 
            ? JSON.parse(agent.language_skills || '[]') 
            : (agent.language_skills || []),
          departments: typeof agent.departments === 'string' 
            ? JSON.parse(agent.departments || '[]') 
            : (agent.departments || []),
          modules: typeof agent.modules === 'string' 
            ? JSON.parse(agent.modules || '[]') 
            : (agent.modules || []),
          country: typeof agent.country === 'string' 
            ? JSON.parse(agent.country || '[]') 
            : (agent.country || []),
          availability_days: typeof agent.availability_days === 'string' 
            ? JSON.parse(agent.availability_days || '[]') 
            : (agent.availability_days || []),
          // ✅ Parse menu_permissions
          menu_permissions: typeof agent.menu_permissions === 'string'
            ? JSON.parse(agent.menu_permissions || '[]')
            : (agent.menu_permissions || [])
        }));
        
        setAgents(parsedAgents);
        setError(null);
      }
    } else if (response.status === 401) {
      localStorage.removeItem('token');
      navigate('/login');
    } else {
      setError('Failed to load agents');
    }
  } catch (err) {
    console.error('Error fetching agents:', err);
    setError('Network error. Please try again.');
  } finally {
    setLoading(false);
  }
};

const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSaveMessage('');
    
    const token = localStorage.getItem('token');
    
    // 🔍 DEBUG: Log departments before sending
    console.log('🔵 handleSubmit - formData.departments:', formData.departments);
    console.log('🔵 handleSubmit - Full formData:', formData);
    console.log('🔵 handleSubmit - Departments count:', formData.departments?.length || 0);
    
    try {
      const url = editingAgent 
        ? `/api/v1/agents/${editingAgent.id}`
        : '/api/v1/agents';
      
      const method = editingAgent ? 'PUT' : 'POST';
      
      console.log('🔵 Sending request to:', url, 'with method:', method);
      console.log('🔵 Request body:', JSON.stringify(formData, null, 2));
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      console.log('🔵 Response received:', data);
      
      if (response.ok && data.success) {
        // Save notification settings if editing
        if (editingAgent) {
          await saveNotificationSettings(editingAgent.id);
        } else if (data.agent && data.agent.id) {
          // For new agent, save notification settings with the new ID
          await saveNotificationSettings(data.agent.id);
        }
        
        showNotification(
          editingAgent ? 'Agent updated successfully' : 'Agent created successfully',
          'success'
        );
        setShowForm(false);
        setEditingAgent(null);
        resetForm();
        resetNotificationSettings();
        fetchAgents();
      } else {
        showNotification(data.message || 'Failed to save agent', 'error');
      }
    } catch (err) {
      console.error('❌ Error saving agent:', err);
      showNotification('Network error. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
};

 const handleEdit = (agent) => {
    console.log('🔵 handleEdit called with agent:', agent);
    console.log('🔵 agent.departments:', agent.departments);
   
    setEditingAgent(agent);
    const newFormData = {
      username: agent.username,
      password: '',
      full_name: agent.full_name,
      email: agent.email || '',
      phone: agent.phone || '',
      phone_whatsapp: agent.phone_whatsapp || '',
      timezone: agent.timezone || 'Africa/Cairo',
      role: agent.role,
      language_skills: Array.isArray(agent.language_skills) ? agent.language_skills : 
                       (typeof agent.language_skills === 'string' ? JSON.parse(agent.language_skills) : []),
      departments: Array.isArray(agent.departments) ? agent.departments : 
                   (typeof agent.departments === 'string' ? JSON.parse(agent.departments) : []),
      modules: Array.isArray(agent.modules) ? agent.modules : 
               (typeof agent.modules === 'string' ? JSON.parse(agent.modules) : []),
      service_type: agent.service_type || 'all',
      country: Array.isArray(agent.country) ? agent.country :
               (typeof agent.country === 'string' ? JSON.parse(agent.country) : []),
      availability_time_from: agent.availability_time_from || '09:00',
      availability_time_to: agent.availability_time_to || '17:00',
      availability_days: Array.isArray(agent.availability_days) ? agent.availability_days :
                         (typeof agent.availability_days === 'string' ? JSON.parse(agent.availability_days) : []),
      max_concurrent_conversations: agent.max_concurrent_conversations || 5,
      auto_assign: agent.auto_assign !== undefined ? agent.auto_assign : true,
      assign_technical: agent.assign_technical ? true : false,
      assign_sales: agent.assign_sales ? true : false,
      manage_others_bookings: agent.manage_others_bookings ? true : false,
      booking_enabled: agent.booking_enabled ? true : false,
      can_view_all_conversations: agent.can_view_all_conversations ? true : false,
      can_view_leads: agent.can_view_leads ? true : false,
      allow_marketing: agent.allow_marketing ? true : false,
      allow_vsm: agent.allow_vsm ? true : false,
      default_module: agent.default_module || 'comms',
      priority_score: agent.priority_score || 50,
      is_active: agent.is_active
    };
    
    console.log('🔵 newFormData.departments:', newFormData.departments);
    setFormData(newFormData);
    
    // Fetch notification settings for this agent
    fetchNotificationSettings(agent.id);
    
    setShowForm(true);
};

  const handleDelete = async (agentId) => {
    if (!window.confirm('Are you sure you want to delete this agent?')) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/api/v1/agents/${agentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        showNotification('Agent deleted successfully', 'success');
        fetchAgents();
      } else {
        showNotification(data.message || 'Failed to delete agent', 'error');
      }
    } catch (err) {
      console.error('Error deleting agent:', err);
      showNotification('Network error. Please try again.', 'error');
    }
  };

  const toggleAgentStatus = async (agentId, currentStatus) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/api/v1/agents/${agentId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        showNotification('Agent status updated', 'success');
        fetchAgents();
      } else {
        showNotification(data.message || 'Failed to update status', 'error');
      }
    } catch (err) {
      console.error('Error updating agent status:', err);
      showNotification('Network error. Please try again.', 'error');
    }
  };

  // 🔐 NEW: Handle opening permissions modal
  const handleEditPermissions = (agent) => {
    setSelectedAgentForPermissions(agent);
    setShowPermissionsModal(true);
  };

  // 🔐 NEW: Save permissions via API
  const handleSavePermissions = async (permissions) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/api/v1/agents/${selectedAgentForPermissions.id}/menu-permissions`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ menu_permissions: permissions })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        // Update local state
        setAgents(prev => prev.map(a => 
          a.id === selectedAgentForPermissions.id 
            ? { ...a, menu_permissions: permissions }
            : a
        ));
        setShowPermissionsModal(false);
        setSelectedAgentForPermissions(null);
        showNotification(`Permissions updated for ${selectedAgentForPermissions.full_name}`, 'success');
      } else {
        showNotification(data.message || 'Failed to update permissions', 'error');
      }
    } catch (err) {
      console.error('Error saving permissions:', err);
      showNotification('Network error. Please try again.', 'error');
    }
  };

  // ✅ Updated resetForm with country as empty array
  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      full_name: '',
      email: '',
      phone: '',
      phone_whatsapp: '',
      timezone: 'Africa/Cairo',
      role: 'agent',
      language_skills: [],
      departments: [],
      modules: [],
      service_type: 'all',
      country: [],  // ✅ Empty array
      availability_time_from: '09:00',
      availability_time_to: '17:00',
      availability_days: [],
      max_concurrent_conversations: 5,
      auto_assign: true,
      assign_technical: false,
      assign_sales: false,
    assign_technical: false,
    assign_sales: false,
      manage_others_bookings: false,
    manage_others_bookings: false,
      priority_score: 50,
      is_active: true
    });
  };

  // ✅ Helper function for checkbox arrays
 const toggleArrayItem = (arrayName, item) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].includes(item)
        ? prev[arrayName].filter(i => i !== item)
        : [...prev[arrayName], item]
    }));
  };

  // NEW: Toggle department object (not just code)
const toggleDepartment = (deptId) => {
    console.log('🟢 toggleDepartment called with deptId:', deptId);
    console.log('🟢 Current formData.departments BEFORE:', formData.departments);
    
    setFormData(prev => {
      const currentDepts = prev.departments || [];
      const exists = currentDepts.find(d => d.id === deptId);
      
      let newDepts;
      if (exists) {
        newDepts = currentDepts.filter(d => d.id !== deptId);
        console.log('🔴 REMOVED department', deptId, '- New array:', newDepts);
      } else {
        newDepts = [...currentDepts, { id: deptId, can_view_all: false }];
        console.log('🟢 ADDED department', deptId, '- New array:', newDepts);
      }
      
      return {
        ...prev,
        departments: newDepts
      };
    });
};

  const showNotification = (message, type) => {
    const notification = document.createElement('div');
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed; top: 20px; right: 20px; padding: 15px 20px;
      background-color: ${type === 'success' ? '#28a745' : '#dc3545'};
      color: white; border-radius: 5px; z-index: 10000;
      animation: slideIn 0.3s ease-out;
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease-out';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  };

  const getRoleBadgeColor = (role) => {
    const colors = { 'admin': '#dc3545', 'supervisor': '#ffc107', 'agent': '#007bff' };
    return colors[role] || '#6c757d';
  };

  return (
    <Layout>
      <div style={{ padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <div>
            <h1 style={{ margin: '0 0 10px 0' }}>{f.page_title}</h1>
            <p style={{ margin: 0, color: '#9ca3af' }}>{f.page_subtitle}</p>
          </div>
          <button onClick={() => { setShowForm(true); setEditingAgent(null); resetForm(); resetNotificationSettings(); }}
            style={{ padding: '12px 24px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: '500' }}>
            {f.add_new_agent}
          </button>
        </div>

        {loading && <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>{f.loading_agents}</div>}
        {error && <div style={{ padding: '20px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '8px', marginBottom: '20px' }}>{error}</div>}

        {!loading && !error && (
          <div style={{ backgroundColor: 'rgba(26, 32, 56, 0.8)', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.3)', overflow: 'hidden', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            {agents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
                <h3>{f.no_agents_found}</h3>
                <p>{f.no_agents_help}</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.2)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>{f.col_name}</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>{f.col_username}</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>{f.col_email}</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>{f.col_role}</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>{f.col_status}</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>{f.col_actions}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map(agent => (
                      <tr key={agent.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        <td style={{ padding: '15px' }}>
                          <div style={{ fontWeight: '500' }}>{agent.full_name}</div>
                          {agent.phone && <div style={{ fontSize: '12px', color: '#9ca3af' }}>{agent.phone}</div>}
                        </td>
                        <td style={{ padding: '15px' }}>{agent.username}</td>
                        <td style={{ padding: '15px' }}>{agent.email || '-'}</td>
                        <td style={{ padding: '15px' }}>
                          <span style={{ padding: '4px 12px', backgroundColor: getRoleBadgeColor(agent.role), color: 'white', borderRadius: '12px', fontSize: '12px', fontWeight: '500', textTransform: 'capitalize' }}>
                            {agent.role}
                          </span>
                        </td>
                        <td style={{ padding: '15px' }}>
                          <button onClick={() => toggleAgentStatus(agent.id, agent.is_active)}
                            style={{ padding: '4px 12px', backgroundColor: agent.is_active ? '#28a745' : '#6c757d', color: 'white', border: 'none', borderRadius: '12px', fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}>
                            {agent.is_active ? f.status_active : f.status_inactive}
                          </button>
                        </td>
                        <td style={{ padding: '15px' }}>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button onClick={() => handleEdit(agent)} 
                              style={{ padding: '6px 12px', backgroundColor: '#ffc107', color: '#f9fafb', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '13px' }}>
                              Edit
                            </button>
                            
                            {/* 🔐 NEW: Permissions Button - Only for Admin/Supervisor */}
                            {(currentUser.role === 'admin' || currentUser.role === 'supervisor') && (
                              <button 
                                onClick={() => handleEditPermissions(agent)}
                                style={{ 
                                  padding: '6px 12px', 
                                  backgroundColor: '#6f42c1', 
                                  color: 'white', 
                                  border: 'none', 
                                  borderRadius: '5px', 
                                  cursor: 'pointer', 
                                  fontSize: '13px',
                                  whiteSpace: 'nowrap'
                                }}
                                title="Edit Menu Permissions">
                                🔐 Permissions
                              </button>
                            )}
                            
                            {/* ⭐ زر جديد - إدارة جدول العمل */}
                            <button 
                              onClick={() => navigate(`/staff-management?agent=${agent.id}`)}      
                              style={{ 
                                padding: '6px 12px', 
                                backgroundColor: '#17a2b8', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '5px', 
                                cursor: 'pointer', 
                                fontSize: '13px',
                                whiteSpace: 'nowrap'
                              }}
                              title="البروفيل">
                              {f.btn_schedule}
                            </button>
                            
                            <button onClick={() => handleDelete(agent.id)} 
                              style={{ padding: '6px 12px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '13px' }}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* 🔐 NEW: Permissions Modal */}
        {showPermissionsModal && selectedAgentForPermissions && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}>
            <div style={{ position: 'relative' }}>
              {/* Agent Name Header */}
              <div style={{
                position: 'absolute',
                top: '-40px',
                left: '0',
                right: '0',
                textAlign: 'center',
                color: '#f9fafb',
                fontSize: '16px',
                fontWeight: '600'
              }}>
                Editing permissions for: {selectedAgentForPermissions.full_name}
              </div>
              
              <MenuPermissionsEditor
                agentId={selectedAgentForPermissions.id}
                currentPermissions={
                  Array.isArray(selectedAgentForPermissions.menu_permissions)
                    ? selectedAgentForPermissions.menu_permissions
                    : (typeof selectedAgentForPermissions.menu_permissions === 'string'
                        ? JSON.parse(selectedAgentForPermissions.menu_permissions || '[]')
                        : [])
                }
                onSave={handleSavePermissions}
                onCancel={() => {
                  setShowPermissionsModal(false);
                  setSelectedAgentForPermissions(null);
                }}
              />
            </div>
          </div>
        )}

        {/* ✅ ENHANCED MODAL WITH ALL NEW FIELDS + MULTI-SELECT COUNTRY */}
        {showForm && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
            <div style={{ backgroundColor: 'rgba(26, 32, 56, 0.95)', padding: '30px', borderRadius: '16px', width: '95%', maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.4)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <h2 style={{ marginTop: 0, marginBottom: '24px', fontSize: '24px', fontWeight: '700' }}>
                {editingAgent ? `✏️ ${f.edit_agent}` : `➕ ${f.create_new_agent}`}
              </h2>

              <form onSubmit={handleSubmit}>
                {/* Basic Information */}
                <div style={{ background: 'rgba(26, 32, 56, 0.95)', padding: '20px', borderRadius: '12px', marginBottom: '20px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#f9fafb' }}>📋 {f.basic_information}</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>{f.full_name} *</label>
                      <input type="text" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} required
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid rgba(255, 255, 255, 0.2)', borderRadius: '8px', fontSize: '14px' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>{f.username} *</label>
                      <input type="text" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} required disabled={editingAgent}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid rgba(255, 255, 255, 0.2)', borderRadius: '8px', fontSize: '14px', backgroundColor: editingAgent ? '#1a2038' : 'transparent' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Email</label>
                      <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid rgba(255, 255, 255, 0.2)', borderRadius: '8px', fontSize: '14px' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>{f.phone}</label>
                      <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+966xxxxxxxxx"
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid rgba(255, 255, 255, 0.2)', borderRadius: '8px', fontSize: '14px' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>{f.whatsapp_number}</label>
                      <input type="tel" value={formData.phone_whatsapp} onChange={(e) => setFormData({ ...formData, phone_whatsapp: e.target.value })} placeholder="+966xxxxxxxxx"
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid rgba(255, 255, 255, 0.2)', borderRadius: '8px', fontSize: '14px' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>{f.role} *</label>
                      <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} required
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid rgba(255, 255, 255, 0.2)', borderRadius: '8px', fontSize: '14px', backgroundColor: '#1a2038' }}>
                        <option value="agent">{f.role_agent}</option>
                        <option value="supervisor">{f.role_supervisor}</option>
                        <option value="admin">{f.role_admin}</option>
                      </select>
                    </div>
                    {!editingAgent && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>{f.password} *</label>
                        <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required={!editingAgent} placeholder={f.password_placeholder}
                          style={{ width: '100%', padding: '10px 12px', border: '2px solid rgba(255, 255, 255, 0.2)', borderRadius: '8px', fontSize: '14px' }} />
                      </div>
                    )}
                  </div>
                </div>

                {/* Work Settings */}
                <div style={{ background: 'rgba(26, 32, 56, 0.95)', padding: '20px', borderRadius: '12px', marginBottom: '20px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#f9fafb' }}>⏰ {f.work_settings}</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>{f.service_type}</label>
                      <select value={formData.service_type} onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid rgba(255, 255, 255, 0.2)', borderRadius: '8px', fontSize: '14px', backgroundColor: '#1a2038' }}>
                        <option value="all">{f.service_all}</option>
                        <option value="sales">{f.service_sales}</option>
                        <option value="support">{f.service_support}</option>
                      </select>
                    </div>
                    <div>
                    </div>
                    <div>
                      <label style={{ display: "block", marginBottom: "6px", fontWeight: "500", fontSize: "14px" }}>🌍 {f.timezone}</label>
                      <select value={formData.timezone} onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                        style={{ width: "100%", padding: "10px 12px", border: "2px solid rgba(255, 255, 255, 0.2)", borderRadius: "8px", fontSize: "14px", backgroundColor: "#1a2038" }}>
                        <option value="Africa/Cairo">🇪🇬 Egypt (Cairo)</option>
                        <option value="Asia/Riyadh">🇸🇦 Saudi Arabia (Riyadh)</option>
                        <option value="Asia/Kuwait">🇰🇼 Kuwait</option>
                        <option value="Asia/Dubai">🇦🇪 UAE (Dubai)</option>
                        <option value="Asia/Amman">🇯🇴 Jordan (Amman)</option>
                        <option value="Asia/Beirut">🇱🇧 Lebanon (Beirut)</option>
                        <option value="Asia/Baghdad">🇮🇶 Iraq (Baghdad)</option>
                        <option value="Africa/Casablanca">🇲🇦 Morocco (Casablanca)</option>
                        <option value="Africa/Tunis">🇹🇳 Tunisia (Tunis)</option>
                        <option value="Europe/London">🇬🇧 UK (London)</option>
                        <option value="Europe/Berlin">🇩🇪 Germany (Berlin)</option>
                        <option value="America/New_York">🇺🇸 US East (New York)</option>
                        <option value="UTC">🌐 UTC</option>
                      </select>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>{f.priority_score}</label>
                      <input type="number" min="0" max="100" value={formData.priority_score} onChange={(e) => setFormData({ ...formData, priority_score: parseInt(e.target.value) || 50 })}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid rgba(255, 255, 255, 0.2)', borderRadius: '8px', fontSize: '14px' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>{f.max_conversations}</label>
                      <input type="number" min="1" max="20" value={formData.max_concurrent_conversations} onChange={(e) => setFormData({ ...formData, max_concurrent_conversations: parseInt(e.target.value) || 5 })}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid rgba(255, 255, 255, 0.2)', borderRadius: '8px', fontSize: '14px' }} />
                    </div>
                  </div>



                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>{f.work_start_time}</label>
                      <input type="time" value={formData.availability_time_from} onChange={(e) => setFormData({ ...formData, availability_time_from: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid rgba(255, 255, 255, 0.2)', borderRadius: '8px', fontSize: '14px' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>{f.work_end_time}</label>
                      <input type="time" value={formData.availability_time_to} onChange={(e) => setFormData({ ...formData, availability_time_to: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', border: '2px solid rgba(255, 255, 255, 0.2)', borderRadius: '8px', fontSize: '14px' }} />
                    </div>
                  </div>
                </div>

                {/* Auto Assign Bot Conversation */}
                <div style={{ background: 'rgba(26, 32, 56, 0.95)', padding: '20px', borderRadius: '12px', marginBottom: '20px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#f9fafb' }}>🤖 {f.auto_assign_bot}</h3>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={formData.auto_assign} onChange={(e) => setFormData({ ...formData, auto_assign: e.target.checked })}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                      <span style={{ fontSize: '14px', fontWeight: '500' }}>✅ {f.enable_auto_assignment}</span>
                    </label>
                  </div>
                  {formData.auto_assign && (
                    <>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>{f.conversation_types}</label>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          <label style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 16px', backgroundColor: formData.assign_technical ? '#10b981' : 'rgba(15, 23, 42, 0.9)', color: formData.assign_technical ? 'white' : '#d1d5db', border: `2px solid ${formData.assign_technical ? '#10b981' : 'rgba(255, 255, 255, 0.2)'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', transition: 'all 0.2s' }}>
                            <input type="checkbox" checked={formData.assign_technical} onChange={(e) => setFormData({ ...formData, assign_technical: e.target.checked })} style={{ display: 'none' }} />
                            🔧 {f.technical}
                          </label>
                          <label style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 16px', backgroundColor: formData.assign_sales ? '#667eea' : 'rgba(15, 23, 42, 0.9)', color: formData.assign_sales ? 'white' : '#d1d5db', border: `2px solid ${formData.assign_sales ? '#667eea' : 'rgba(255, 255, 255, 0.2)'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', transition: 'all 0.2s' }}>
                            <input type="checkbox" checked={formData.assign_sales} onChange={(e) => setFormData({ ...formData, assign_sales: e.target.checked })} style={{ display: 'none' }} />
                            💼 {f.sales}
                          </label>
                        </div>
                        <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#9ca3af' }}>{f.conv_types_help}</p>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>{f.countries}</label>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          {[{code: 'KW', name: `🇰🇼 ${f.country_kw}`}, {code: 'SA', name: `🇸🇦 ${f.country_sa}`}, {code: 'EG', name: `🇪🇬 ${f.country_eg}`}, {code: 'OTHER', name: `🌍 ${f.country_other}`}].map(c => (
                            <label key={c.code} style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 16px', backgroundColor: formData.country.includes(c.code) ? '#f59e0b' : 'rgba(15, 23, 42, 0.9)', color: formData.country.includes(c.code) ? 'white' : '#d1d5db', border: `2px solid ${formData.country.includes(c.code) ? '#f59e0b' : 'rgba(255, 255, 255, 0.2)'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', transition: 'all 0.2s' }}>
                              <input type="checkbox" checked={formData.country.includes(c.code)} onChange={() => toggleArrayItem('country', c.code)} style={{ display: 'none' }} />
                              {c.name}
                            </label>
                          ))}
                        </div>
                        <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#9ca3af' }}>{f.countries_help}</p>
                      </div>
                    </>
                  )}
                </div>

                {/* Permissions */}
                <div style={{ background: 'rgba(26, 32, 56, 0.95)', padding: '20px', borderRadius: '12px', marginBottom: '20px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#f9fafb' }}>🔐 {f.permissions}</h3>
                  <div style={{ marginTop: "12px" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input type="checkbox" checked={formData.manage_others_bookings} onChange={(e) => setFormData({ ...formData, manage_others_bookings: e.target.checked })}
                        style={{ width: "18px", height: "18px", cursor: "pointer" }} />
                      <span style={{ fontSize: "14px", fontWeight: "500" }}>📅 إدارة مواعيد الغير</span>
                    </label>
                  </div>
                  <div style={{ marginTop: "12px" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input type="checkbox" checked={formData.booking_enabled} onChange={(e) => setFormData({ ...formData, booking_enabled: e.target.checked })}
                        style={{ width: "18px", height: "18px", cursor: "pointer" }} />
                      <span style={{ fontSize: "14px", fontWeight: "500" }}>📆 تفعيل نظام الحجز</span>
                    </label>
                  </div>
                  <div style={{ marginTop: "12px" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input type="checkbox" checked={formData.can_view_all_conversations} onChange={(e) => setFormData({ ...formData, can_view_all_conversations: e.target.checked })}
                        style={{ width: "18px", height: "18px", cursor: "pointer" }} />
                      <span style={{ fontSize: "14px", fontWeight: "500" }}>💬 رؤية جميع المحادثات</span>
                    </label>
                  </div>
                  <div style={{ marginTop: "12px" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input type="checkbox" checked={formData.can_view_leads} onChange={(e) => setFormData({ ...formData, can_view_leads: e.target.checked })}
                        style={{ width: "18px", height: "18px", cursor: "pointer" }} />
                      <span style={{ fontSize: "14px", fontWeight: "500" }}>🛒 إدارة Leads</span>
                    </label>
                  </div>
                  <div style={{ marginTop: "12px" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input type="checkbox" checked={formData.allow_marketing} onChange={(e) => setFormData({ ...formData, allow_marketing: e.target.checked })}
                        style={{ width: "18px", height: "18px", cursor: "pointer" }} />
                      <span style={{ fontSize: "14px", fontWeight: "500" }}>📢 حملات التسويق</span>
                    </label>
                  </div>
                  <div style={{ marginTop: "12px" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input type="checkbox" checked={formData.allow_vsm} onChange={(e) => setFormData({ ...formData, allow_vsm: e.target.checked })}
                        style={{ width: "18px", height: "18px", cursor: "pointer" }} />
                      <span style={{ fontSize: "14px", fontWeight: "500" }}>🚗 إدارة صيانة المركبات</span>
                    </label>
                  </div>
                  <div style={{ marginTop: "12px" }}>
                    <label style={{ display: "block", fontSize: "14px", fontWeight: "500", marginBottom: "6px" }}>🏠 الصفحة الافتراضية بعد الدخول</label>
                    <select value={formData.default_module} onChange={(e) => setFormData({ ...formData, default_module: e.target.value })}
                      style={{ width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.2)", backgroundColor: "rgba(255,255,255,0.05)", color: "#fff", fontSize: "14px", cursor: "pointer" }}>
                      <option value="comms">💬 المحادثات (Comms)</option>
                      <option value="helpdesk">🎫 الدعم الفني (Helpdesk)</option>
                      <option value="fsm">🔧 الخدمة الميدانية (FSM)</option>
                    </select>
                  </div>
                </div>

                {/* Skills & Specializations */}
                <div style={{ background: 'rgba(26, 32, 56, 0.95)', padding: '20px', borderRadius: '12px', marginBottom: '20px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#f9fafb' }}>🎯 {f.skills_specializations}</h3>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>{f.languages}</label>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      {['ar', 'en'].map(lang => (
                        <label key={lang} style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 16px', backgroundColor: formData.language_skills.includes(lang) ? '#667eea' : 'rgba(15, 23, 42, 0.9)', color: formData.language_skills.includes(lang) ? 'white' : '#d1d5db', border: `2px solid ${formData.language_skills.includes(lang) ? '#667eea' : 'rgba(255, 255, 255, 0.2)'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', transition: 'all 0.2s' }}>
                          <input type="checkbox" checked={formData.language_skills.includes(lang)} onChange={() => toggleArrayItem('language_skills', lang)} style={{ display: 'none' }} />
                          {lang === 'ar' ? '🇸🇦 Arabic' : '🇺🇸 English'}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>{f.helpdesk_departments}</label>
                    {helpdeskDepartments.length === 0 ? (
                      <div style={{ padding: '12px', backgroundColor: 'rgba(255, 193, 7, 0.2)', borderRadius: '8px', color: '#ffc107', fontSize: '14px' }}>
                        ⚠️ No helpdesk departments found
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        {helpdeskDepartments.map(dept => {
                          const isSelected = formData.departments.some(d => d.id === dept.id);
                          return (
                            <label key={dept.id} style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 16px', backgroundColor: isSelected ? '#28a745' : 'rgba(15, 23, 42, 0.9)', color: isSelected ? 'white' : '#d1d5db', border: `2px solid ${isSelected ? '#28a745' : 'rgba(255, 255, 255, 0.2)'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', transition: 'all 0.2s' }}>
                              <input type="checkbox" checked={isSelected} onChange={() => toggleDepartment(dept.id)} style={{ display: 'none' }} />
                              🏢 {dept.name}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>ERP Modules</label>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      {[{code: 'all', name: '📦 All'}, {code: 'inventory', name: '📦 Inventory'}, {code: 'accounting', name: '💵 Accounting'}, {code: 'sales', name: '🛒 Sales'}, {code: 'hr', name: '👥 HR'}, {code: 'pos', name: '🏪 POS'}].map(module => (
                        <label key={module.code} style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 16px', backgroundColor: formData.modules.includes(module.code) ? '#17a2b8' : 'rgba(15, 23, 42, 0.9)', color: formData.modules.includes(module.code) ? 'white' : '#d1d5db', border: `2px solid ${formData.modules.includes(module.code) ? '#17a2b8' : 'rgba(255, 255, 255, 0.2)'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', transition: 'all 0.2s' }}>
                          <input type="checkbox" checked={formData.modules.includes(module.code)} onChange={() => toggleArrayItem('modules', module.code)} style={{ display: 'none' }} />
                          {module.name}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>Work Days</label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {[{code: 'sunday', name: 'Sun'}, {code: 'monday', name: 'Mon'}, {code: 'tuesday', name: 'Tue'}, {code: 'wednesday', name: 'Wed'}, {code: 'thursday', name: 'Thu'}, {code: 'friday', name: 'Fri'}, {code: 'saturday', name: 'Sat'}].map(day => (
                        <label key={day.code} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '60px', padding: '8px 12px', backgroundColor: formData.availability_days.includes(day.code) ? '#ffc107' : 'rgba(15, 23, 42, 0.9)', color: formData.availability_days.includes(day.code) ? '#212529' : '#d1d5db', border: `2px solid ${formData.availability_days.includes(day.code) ? '#ffc107' : 'rgba(255, 255, 255, 0.2)'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', transition: 'all 0.2s' }}>
                          <input type="checkbox" checked={formData.availability_days.includes(day.code)} onChange={() => toggleArrayItem('availability_days', day.code)} style={{ display: 'none' }} />
                          {day.name}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 🔔 Helpdesk Notification Settings */}
                <div style={{ background: 'rgba(26, 32, 56, 0.95)', padding: '20px', borderRadius: '12px', marginBottom: '20px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#f9fafb' }}>
                    🔔 Helpdesk Notification Settings
                  </h3>
                  
                  {loadingNotifications ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>Loading notification settings...</div>
                  ) : (
                    <>
                      {/* Enable Notifications Toggle */}
                      <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: notificationSettings.is_active ? 'rgba(40, 167, 69, 0.2)' : 'rgba(108, 117, 125, 0.2)', borderRadius: '8px', border: `2px solid ${notificationSettings.is_active ? '#28a745' : 'rgba(255, 255, 255, 0.2)'}` }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                          <input 
                            type="checkbox" 
                            checked={notificationSettings.is_active} 
                            onChange={(e) => setNotificationSettings({ ...notificationSettings, is_active: e.target.checked })}
                            style={{ width: '20px', height: '20px', cursor: 'pointer' }} 
                          />
                          <span style={{ fontSize: '15px', fontWeight: '600', color: notificationSettings.is_active ? '#28a745' : '#9ca3af' }}>
                            {notificationSettings.is_active ? '✅ Notifications Enabled' : '❌ Notifications Disabled'}
                          </span>
                        </label>
                      </div>

                      {/* Notification Options - Only show when enabled */}
                      {notificationSettings.is_active && (
                        <>
                          {/* Notification Channels */}
                          <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500', fontSize: '14px', color: '#d1d5db' }}>
                              📬 Notification Channels
                            </label>
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                              <label style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '10px 16px',
                                backgroundColor: notificationSettings.notify_by_email ? '#007bff' : 'rgba(15, 23, 42, 0.9)',
                                color: notificationSettings.notify_by_email ? 'white' : '#d1d5db',
                                border: `2px solid ${notificationSettings.notify_by_email ? '#007bff' : 'rgba(255, 255, 255, 0.2)'}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                                transition: 'all 0.2s'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={notificationSettings.notify_by_email}
                                  onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_by_email: e.target.checked })}
                                  style={{ display: 'none' }}
                                />
                                📧 Email
                              </label>
                              <label style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '10px 16px',
                                backgroundColor: notificationSettings.notify_by_whatsapp ? '#25d366' : 'rgba(15, 23, 42, 0.9)',
                                color: notificationSettings.notify_by_whatsapp ? 'white' : '#d1d5db',
                                border: `2px solid ${notificationSettings.notify_by_whatsapp ? '#25d366' : 'rgba(255, 255, 255, 0.2)'}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                                transition: 'all 0.2s'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={notificationSettings.notify_by_whatsapp}
                                  onChange={(e) => setNotificationSettings({ ...notificationSettings, notify_by_whatsapp: e.target.checked })}
                                  style={{ display: 'none' }}
                                />
                                📱 WhatsApp
                              </label>
                            </div>
                          </div>

                          {/* Ticket Events */}
                          <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500', fontSize: '14px', color: '#d1d5db' }}>
                              🎫 Ticket Events
                            </label>
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                              {[
                                { key: 'receive_new_ticket', label: '🆕 New Ticket', color: '#6f42c1' },
                                { key: 'receive_ticket_assigned', label: '📌 Assigned to Me', color: '#007bff' },
                                { key: 'receive_ticket_unassigned', label: '📤 Unassigned', color: '#6c757d' },
                                { key: 'receive_ticket_reopened', label: '🔄 Reopened', color: '#e83e8c' },
                                { key: 'receive_client_reply', label: '💬 Client Reply', color: '#17a2b8' },
                                { key: 'receive_status_change', label: '🔄 Status Change', color: '#ffc107' }
                              ].map(item => (
                                <label key={item.key} style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '8px 14px',
                                  backgroundColor: notificationSettings[item.key] ? item.color : 'rgba(15, 23, 42, 0.9)',
                                  color: notificationSettings[item.key] ? 'white' : '#d1d5db',
                                  border: `2px solid ${notificationSettings[item.key] ? item.color : 'rgba(255, 255, 255, 0.2)'}`,
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: '500',
                                  transition: 'all 0.2s'
                                }}>
                                  <input
                                    type="checkbox"
                                    checked={notificationSettings[item.key]}
                                    onChange={(e) => setNotificationSettings({ ...notificationSettings, [item.key]: e.target.checked })}
                                    style={{ display: 'none' }}
                                  />
                                  {item.label}
                                </label>
                              ))}
                            </div>
                          </div>

                          {/* SLA Events */}
                          <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500', fontSize: '14px', color: '#d1d5db' }}>
                              ⏰ SLA & Urgency
                            </label>
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                              <label style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '8px 14px',
                                backgroundColor: notificationSettings.receive_sla_escalation ? '#dc3545' : 'rgba(15, 23, 42, 0.9)',
                                color: notificationSettings.receive_sla_escalation ? 'white' : '#d1d5db',
                                border: `2px solid ${notificationSettings.receive_sla_escalation ? '#dc3545' : 'rgba(255, 255, 255, 0.2)'}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '500',
                                transition: 'all 0.2s'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={notificationSettings.receive_sla_escalation}
                                  onChange={(e) => setNotificationSettings({ ...notificationSettings, receive_sla_escalation: e.target.checked })}
                                  style={{ display: 'none' }}
                                />
                                ⏰ SLA Breach Warning
                              </label>
                              <label style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '8px 14px',
                                backgroundColor: notificationSettings.receive_dept_delayed ? '#e67e22' : 'rgba(15, 23, 42, 0.9)',
                                color: notificationSettings.receive_dept_delayed ? 'white' : '#d1d5db',
                                border: `2px solid ${notificationSettings.receive_dept_delayed ? '#e67e22' : 'rgba(255, 255, 255, 0.2)'}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '500',
                                transition: 'all 0.2s'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={notificationSettings.receive_dept_delayed}
                                  onChange={(e) => setNotificationSettings({ ...notificationSettings, receive_dept_delayed: e.target.checked })}
                                  style={{ display: 'none' }}
                                />
                                ⏳ Delayed
                              </label>
                              <label style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '8px 14px',
                                backgroundColor: notificationSettings.receive_dept_overdue ? '#fd7e14' : 'rgba(15, 23, 42, 0.9)',
                                color: notificationSettings.receive_dept_overdue ? 'white' : '#d1d5db',
                                border: `2px solid ${notificationSettings.receive_dept_overdue ? '#fd7e14' : 'rgba(255, 255, 255, 0.2)'}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '500',
                                transition: 'all 0.2s'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={notificationSettings.receive_dept_overdue}
                                  onChange={(e) => setNotificationSettings({ ...notificationSettings, receive_dept_overdue: e.target.checked })}
                                  style={{ display: 'none' }}
                                />
                                🟠 Overdue
                              </label>
                              <label style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '8px 14px',
                                backgroundColor: notificationSettings.receive_dept_critical ? '#dc3545' : 'rgba(15, 23, 42, 0.9)',
                                color: notificationSettings.receive_dept_critical ? 'white' : '#d1d5db',
                                border: `2px solid ${notificationSettings.receive_dept_critical ? '#dc3545' : 'rgba(255, 255, 255, 0.2)'}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '500',
                                transition: 'all 0.2s'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={notificationSettings.receive_dept_critical}
                                  onChange={(e) => setNotificationSettings({ ...notificationSettings, receive_dept_critical: e.target.checked })}
                                  style={{ display: 'none' }}
                                />
                                🔴 Critical
                              </label>
                            </div>
                          </div>

                          
                         
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                  <button type="button" onClick={() => { setShowForm(false); setEditingAgent(null); resetNotificationSettings(); }}
                    style={{ padding: '12px 24px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s' }}>
                    ✕ Cancel
                  </button>
                  <button type="submit"
                    style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s' }}>
                    {editingAgent ? '✓ Update Agent' : '➕ Create Agent'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Agents;